import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Volume2, VolumeX, Mic, MicOff, Wifi, WifiOff, Brain, BrainCircuit, Moon, Sun } from 'lucide-react';

const ChatHeader = ({ 
  isVoiceEnabled, 
  onVoiceToggle, 
  onVoiceStart, 
  firebaseConnected, 
  ollamaAvailable, 
  systemSettings, 
  darkMode
}) => {
  const { t, i18n } = useTranslation();

  // Get AI assistant settings with fallbacks
  const aiAssistant = systemSettings?.aiAssistant || {
    name: 'Jeje',
    avatarUrl: '/jeje_bot.png',
    description: 'AI Assistant from Scalovate'
  };
  
  // Get custom styling from CMS settings
  const chatHeaderBackgroundColor = systemSettings?.branding?.chatHeaderBackgroundColor || 'linear-gradient(to right, rgba(255, 255, 255, 0), rgba(200, 200, 200, 0.5))';
  const chatHeaderTextColor = systemSettings?.branding?.chatHeaderTextColor || '#1f2937';
  const chatHeaderTransparency = systemSettings?.branding?.chatHeaderTransparency || 85;
  
  // Convert hex color to rgba with transparency
  const hexToRgba = (hex, alpha) => {
    if (hex.startsWith('linear-gradient') || hex.startsWith('radial-gradient')) {
      return hex; // Return gradient as-is
    }
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha / 100})`;
  };
  
  // In dark mode, override admin colors with dark theme
  const headerStyle = darkMode ? {
    background: 'linear-gradient(135deg, rgba(31, 41, 55, 0.9) 0%, rgba(17, 24, 39, 0.9) 100%)',
    color: '#ffffff',
    backdropFilter: 'blur(10px)',
    borderBottom: '1px solid rgba(55, 65, 81, 0.5)'
  } : {
    background: chatHeaderBackgroundColor.startsWith('linear-gradient') || chatHeaderBackgroundColor.startsWith('radial-gradient')
      ? chatHeaderBackgroundColor
      : `linear-gradient(135deg, ${hexToRgba('#ffffff', chatHeaderTransparency)} 0%, ${hexToRgba(chatHeaderBackgroundColor, chatHeaderTransparency)} 100%)`,
    color: chatHeaderTextColor,
    backdropFilter: 'blur(10px)',
    borderBottom: `1px solid ${hexToRgba(chatHeaderBackgroundColor, 30)}`
  };

  return (
    <motion.div 
      className="backdrop-blur-sm border-b shadow-sm"
      style={headerStyle}
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="p-4 flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className="relative">
            <motion.img 
              src={aiAssistant.avatarUrl || '/jeje_bot.png'} 
              alt={`${aiAssistant.name} AI Assistant`} 
              className="w-14 h-14 rounded-full object-cover border-2 shadow-lg"
              style={{ borderColor: 'var(--primary-color, #ce6161)' }}
              whileHover={{ scale: 1.1 }}
              transition={{ type: "spring", stiffness: 400, damping: 10 }}
              onError={(e) => {
                console.log('Avatar failed to load, using fallback');
                e.target.src = '/jeje_bot.png';
              }}
            />
            <div className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 shadow-sm ${
              darkMode ? 'border-gray-800' : 'border-white'
            } ${firebaseConnected ? 'bg-green-500' : 'bg-orange-500'}`}></div>
          </div>
          <div>
            <h3 
              className="font-semibold text-lg flex items-center gap-2"
              style={{ color: darkMode ? '#ffffff' : chatHeaderTextColor }}
            >
              {aiAssistant.name}
              {firebaseConnected ? (
                <Wifi size={16} className="text-green-500" />
              ) : (
                <WifiOff size={16} className="text-orange-500" />
              )}
              {ollamaAvailable ? (
                <BrainCircuit size={16} className="text-blue-500" title="AI Engine Connected" />
              ) : (
                <Brain size={16} className="text-gray-400" title="AI Engine Offline" />
              )}
            </h3>
            <p 
              className="text-sm opacity-75"
              style={{ color: darkMode ? '#ffffff' : chatHeaderTextColor }}
            >
              {firebaseConnected ? 
                (i18n.language === 'ar' ? 'متصل وجاهز للمساعدة' : 'Connected & Ready to Help') : 
                (i18n.language === 'ar' ? 'وضع عدم الاتصال' : 'Offline Mode')
              }
              {ollamaAvailable && (
                <span className="ml-2 text-blue-500">
                  {i18n.language === 'ar' ? '• محرك الذكاء الاصطناعي نشط' : '• AI Engine Active'}
                </span>
              )}
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          <motion.button
            onClick={onVoiceToggle}
            className={`p-2 rounded-lg transition-all duration-200 ${
              isVoiceEnabled 
                ? 'text-white' 
                : darkMode
                ? 'text-gray-300 hover:text-white hover:bg-white/10'
                : 'text-gray-600 hover:text-gray-800 hover:bg-black/10'
            }`}
            style={isVoiceEnabled ? { 
              backgroundColor: 'var(--primary-color, #ce6161)',
              color: 'white'
            } : {}}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            title={isVoiceEnabled ? t('voice.disable') : t('voice.enable')}
          >
            {isVoiceEnabled ? <Volume2 size={20} /> : <VolumeX size={20} />}
          </motion.button>

          <motion.button
            onClick={onVoiceStart}
            className={`p-2 rounded-lg transition-all duration-200 ${
              darkMode 
                ? 'text-gray-300 hover:text-white hover:bg-white/10' 
                : 'text-gray-600 hover:text-gray-800 hover:bg-black/10'
            }`}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            title={t('voice.startListening')}
          >
            <Mic size={20} />
          </motion.button>
        </div>
      </div>
    </motion.div>
  );
};

export default ChatHeader; 
